import React from 'react';
import styled, { css } from 'styled-components/macro';
import { compose, layout } from 'styled-system';
import { format } from 'date-fns';

import Anchor from 'components/base/Anchor/Anchor';
import ChangeArrow from 'components/base/ChangeArrow/ChangeArrow';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import { definitionLabel, definitionValue } from 'styles/base-classes';

import stringToSlug from 'helpers/stringToSlug';
import formatNumber from 'helpers/formatNumber';
import { ABBREVIATION_THRESHOLD } from 'helpers/abbreviateNumber';
import colors from 'styles/themes/colors';
import getNumberColor from 'helpers/getNumberColor';

export const Label = styled('dt')<any>`
  ${definitionLabel};
  margin-bottom: ${({ theme }): string => theme.spacing.xxsmall};
  ${({ newDesign }): string =>
    newDesign
      ? `font-size: 13px; margin-right: 12px; letter-spacing: normal;`
      : ''}
  ${({ isMobileView }): string =>
    isMobileView ? `font-weight: normal !important;` : ''}
`;

interface IDataProps {
  dataColor?: string;
  showRow?: boolean;
  showColumn?: boolean;
  newDesign?: boolean;
  isMobileView?: boolean;
  unsetWidth?: boolean;
  isKPIChange?: boolean;
  isBlack?: boolean;
  compareMode?: boolean;
  isHeightFixed?: boolean;
}
interface IGroupProps {
  showRow?: boolean;
  showColumn?: boolean;
  isMobileView?: boolean;
  compareNumber?: number;
  isHeightFixed?: boolean;
  mobileRow?: boolean;
}

const StyledChangeArrow = styled('span')<any>`
  color: ${({ isPositive, theme }): string =>
    isPositive ? theme.PositiveChange : theme.NegativeChange};
  display: inline-block;
  ${({ isMobileView }): string => (isMobileView ? 'width: 15px;' : '')}
`;

export const Data = styled('dd')<IDataProps>`
  ${definitionValue};
  ${({ showColumn, isMobileView }): string =>
    showColumn && isMobileView
      ? 'line-height: 4rem !important; height: 20px;'
      : ''}
  color: ${({ theme, dataColor, isKPIChange, isBlack }): string | undefined =>
    isKPIChange && dataColor
      ? theme[dataColor]
      : isBlack
      ? theme.DeepBlue
      : dataColor};
  ${({ compareMode, dataColor }): string =>
    compareMode ? `span{color: ${dataColor} !important;}` : ''}
  margin-top: 0 !important;
  margin-right: 5px;
  ${({ showRow, isMobileView }): string =>
    showRow ? `line-height: ${isMobileView ? '2.2' : '3'}rem;` : ''}
  ${({ unsetWidth }): string =>
    unsetWidth ? 'line-height: 1.2 !important;' : ''}
  ${({ unsetWidth, showColumn, isMobileView }): string =>
    unsetWidth && showColumn && isMobileView
      ? 'span {width: unset;} line-height: 3.5rem !important;'
      : ''}
  height: ${({ isHeightFixed }): string => (isHeightFixed ? '20px' : 'auto')};
`;

const PersentageValue = styled('span')<{ isMobile?: boolean }>`
  font-size: ${({ isMobile }): string => (isMobile ? '0.82em' : '0.6em')};
  padding-left: ${({ theme, isMobile }): string =>
    isMobile ? '0px' : theme.spacing.xsmall};
  font-weight: 600;
  color: ${({ theme }): string => theme.CasperGrey};

  ${({ isMobile }): any =>
    isMobile &&
    css`
      display: block;
    `}
`;

export const Group = styled('div')<IGroupProps>`
  flex-grow: 0;
  flex-shrink: 1;
  flex-basis: auto;
  ${({ showRow }): string =>
    showRow ? 'margin: 0.5em 4em 0.5em 0.5em !important;' : ''}
  ${({ showColumn }): string =>
    showColumn
      ? `display: flex; flex-direction: row; align-items: baseline;`
      : ''}

  ${({ mobileRow }): string =>
    mobileRow ? 'flex-direction: row !important; align-items: flex-end;' : ''}

  ${({ isMobileView, compareNumber }): string =>
    isMobileView
      ? `
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: baseline;
    height: ${compareNumber ? 18 * compareNumber : 18}px;
    span, dt {
      font-size: 16px;
      margin-right: 5px;
      font-weight: 600;
    }
    dt {
      letter-spacing: 1px;
    }
    &:last-child {
      height: ${({ isHeightFixed }: { isHeightFixed: boolean }): string =>
        isHeightFixed ? 'auto' : '10px'}
    }
  `
      : ''};

  @media screen and (max-width: 1150px) {
    ${({ showRow }): string =>
      showRow ? 'margin: 0.5em 3em 0.5em 0.5em !important;' : ''}
  }
  @media screen and (max-width: 1000px) {
    ${({ showRow }): string =>
      showRow ? 'margin: 0.5em 2em 0.5em 0.5em !important;' : ''}
  }
  @media screen and (max-width: 920px) {
    ${({ showRow }): string => (showRow ? 'margin: 0.5em !important;' : '')}
  }
`;

export const Circle = styled('div')<any>`
  width: ${({ width }): string => (width ? width : '9px')};
  display: inline-block;
  height: ${({ height }): string => (height ? height : '9px')};
  border-radius: ${({ borderRadius = '5px' }): string => borderRadius};
  background-color: ${({ color }): string => color};
  opacity: ${({ opacity }): number => opacity};
  margin: ${({ theme, isMobileView }): string =>
    `0 ${isMobileView ? theme.spacing.xsmall : theme.spacing.xxsmall} ${
      theme.spacing.xxsmall
    } 0`};
`;

const RawValue = styled('span')<any>`
  color: ${({ isPositive, theme }): string =>
    isPositive ? theme.PositiveChange : theme.NegativeChange};
  display: inline-block;
`;

const ValuesWrapper = styled('div')<any>`
  ${({ compareMode }): string =>
    compareMode ? 'span {font-weight: 600;}' : ''}
  ${({ isMobileView }): string =>
    isMobileView ? 'display: flex; flex-direction: column;' : ''}
`;

export const Header = styled('h3')<any>`
  ${compose(layout)};
  font-size: 1em;
  ${({ marginBottom }): string =>
    marginBottom ? `margin-bottom: ${marginBottom} !important;` : ''}
  ${({ marginTop }): string =>
    marginTop ? `margin-top: ${marginTop} !important;` : ''}
  font-weight: ${({ titleAlignLeft }): string =>
    titleAlignLeft ? 'bold' : '600'};
`;

interface IValuesProps {
  // The label for the value.
  label?: string;
  // The value to display. It is not required because it can, and may, be null.
  values?: any;
  // A URL to link to
  to?: string;
  // Any color value that CSS supports, used to color the datapoint(s)
  // dataColor may be a string or string[]
  dataColor?: any;
  // Display links with a chevron, only used together with 'to'
  withChevron?: boolean;
  preserveSearch?: boolean;
  // data-pw value
  cy?: string;
  // Should data-pw be shown?
  showCyAttr?: boolean;
  // if there is additional type
  type?: 'number' | 'percentage' | 'trend' | 'newTrend' | 'rawNumber';
  // how to show data
  showRow?: boolean;
  // how to show data
  showColumn?: boolean;
  // how to show data
  newDesign?: boolean;
  // show mobile view
  isMobileView?: boolean;
  // show number view
  isFullNumber?: boolean;
  breakdownAttributes?: any;
  withTooltip?: boolean;
  compareMode?: boolean;
  isKPIValue?: boolean;
  keyValueType?: 'rtd-access-statistics' | undefined;
}

const Values = (props: IValuesProps): any => {
  const {
    label,
    values,
    to,
    dataColor = colors.Blue,
    withChevron = false,
    preserveSearch,
    cy = 'key-value-group-item',
    showCyAttr = false,
    type,
    showRow,
    newDesign,
    isMobileView,
    isFullNumber,
    showColumn,
    breakdownAttributes = {},
    withTooltip = false,
    compareMode,
    isKPIValue,
    keyValueType,
  } = props;

  const { circleColor, circleColorOpacity, percentage } = breakdownAttributes;
  // If the values is a string or a number, display a single values
  if (typeof values === 'string' || typeof values === 'number') {
    if (
      withTooltip &&
      keyValueType === 'rtd-access-statistics' &&
      typeof values === 'number'
    ) {
      const displayValue = formatNumber(
        values,
        isMobileView ? 'full' : 'number',
        isMobileView ? false : true
      );
      const fullValue = formatNumber(values, 'full', false);
      return (
        <Data
          dataColor={dataColor}
          data-pw={showCyAttr ? cy : undefined}
          showRow={showRow}
          showColumn={showColumn}
        >
          {!isMobileView ? (
            <TippyToolTip
              enabled={Math.abs(values) >= ABBREVIATION_THRESHOLD}
              tooltipContent={fullValue}
              canBeCopied={true}
            >
              <>
                {circleColor && (
                  <Circle color={circleColor} opacity={circleColorOpacity} />
                )}
                <span>{displayValue}</span>
                {percentage && <PersentageValue>{percentage}</PersentageValue>}
              </>
            </TippyToolTip>
          ) : (
            <>
              <span>{displayValue}</span>
              {percentage && (
                <PersentageValue isMobile={true}>{percentage}</PersentageValue>
              )}
            </>
          )}
        </Data>
      );
    }

    return (
      <Data
        dataColor={dataColor}
        data-pw={showCyAttr ? cy : undefined}
        showRow={showRow}
        showColumn={showColumn}
      >
        {to ? (
          <Anchor
            to={to}
            withChevron={withChevron}
            preserveSearch={preserveSearch}
          >
            {values}
          </Anchor>
        ) : (
          values
        )}
      </Data>
    );
  }

  // If the values is an Array, display multiple values
  if (Array.isArray(values)) {
    return values.map((valueObject, index) => {
      // This guards against improperly structured data where empty array items get passed
      if (!valueObject) {
        return null;
      }
      // If we pass an array of values, we made sure they are objects which contain the needed information to
      // format them accordingly here
      const { type: valueType, value } = valueObject;

      // If the color is an Array, use the index to pick a color in compare order
      const ordinaryColor = Array.isArray(dataColor)
        ? dataColor[index]
        : dataColor;
      const isKPIChange = isKPIValue && label?.toLowerCase() === 'change';
      const color = compareMode
        ? ordinaryColor
        : isKPIChange
        ? getNumberColor(value)
        : ordinaryColor;

      let displayValue = value;
      let fullValue = value;

      const isFull =
        isFullNumber !== undefined
          ? isFullNumber
          : isMobileView && valueType !== 'trend';

      if (displayValue === null || typeof value === 'number') {
        displayValue = formatNumber(value, isFull ? 'full' : valueType, true);
        fullValue = formatNumber(value, isFull ? 'full' : valueType, false);
      }
      const todayWeekDay = format(new Date(), 'c');
      /** 1 is Sunday, 7 is Saturday
       * We should display the tooltip of unavailable data for streams if today is not Tuesday, Wednesday, Thursday
       */
      const isDayWithStreamsData = [3, 4, 5].every(
        (weekday: number) => Number(todayWeekDay) !== weekday
      );
      const noKPIStreams =
        isDayWithStreamsData &&
        isKPIValue &&
        label === 'Streams' &&
        value === 0;
      const noStreamsAvailableTooltipText = 'Data not yet available';

      if (valueType === 'trend' && newDesign) {
        const isPositive = value > 0;
        return (
          <Data
            data-pw={showCyAttr ? cy : undefined}
            key={stringToSlug(`${label}-${index}`)}
            showRow={showRow}
            showColumn={showColumn}
            isMobileView={isMobileView}
            dataColor={color}
            compareMode={compareMode}
            unsetWidth
          >
            {isMobileView ? (
              <StyledChangeArrow
                isPositive={isPositive}
                isMobileView={isMobileView}
              >{`${displayValue}`}</StyledChangeArrow>
            ) : (
              <StyledChangeArrow
                isPositive={isPositive}
                isMobileView={isMobileView}
              >
                {!compareMode && (
                  <span style={{ fontSize: '12px' }}>
                    {isPositive ? '▲' : '▼'}
                  </span>
                )}
                {` ${displayValue}`}
              </StyledChangeArrow>
            )}
          </Data>
        );
      }

      if (type === 'newTrend') {
        const isPositive = value > 0;
        return (
          <Data
            data-pw={showCyAttr ? cy : undefined}
            key={stringToSlug(`${label}-${index}`)}
            showRow={showRow}
            isMobileView={isMobileView}
            showColumn={showColumn}
            unsetWidth
            isHeightFixed={!!compareMode && isMobileView}
          >
            {isMobileView ? (
              <StyledChangeArrow
                isPositive={isPositive}
                isMobileView={isMobileView}
              >{`(${displayValue})`}</StyledChangeArrow>
            ) : (
              <ChangeArrow changeValue={value} textValue={displayValue} />
            )}
          </Data>
        );
      }

      if (type === 'rawNumber') {
        const isPositive = value > 0;
        return (
          <Data
            data-pw={showCyAttr ? cy : undefined}
            key={stringToSlug(`${label}-${index}`)}
            isMobileView={isMobileView}
          >
            <RawValue isPositive={isPositive}>{displayValue}</RawValue>
          </Data>
        );
      }

      return (
        <Data
          key={stringToSlug(`${label}-${index}`)}
          dataColor={color}
          data-pw={showCyAttr ? cy : undefined}
          showColumn={showColumn}
          isMobileView={isMobileView}
          showRow={showRow}
          newDesign={newDesign}
          isKPIChange={isKPIChange && !compareMode}
          isBlack={!compareMode}
          isHeightFixed={!!compareMode && isMobileView}
        >
          {to ? (
            <Anchor to={to} withChevron={withChevron}>
              <TippyToolTip
                enabled={Math.abs(value) >= ABBREVIATION_THRESHOLD}
                tooltipContent={fullValue}
                canBeCopied={true}
              >
                {isMobileView ? fullValue : displayValue}
              </TippyToolTip>
            </Anchor>
          ) : (
            <TippyToolTip
              enabled={
                Math.abs(value) >= ABBREVIATION_THRESHOLD || noKPIStreams
              }
              tooltipContent={
                noKPIStreams ? noStreamsAvailableTooltipText : fullValue
              }
              canBeCopied={!noKPIStreams}
            >
              {isMobileView ? fullValue : displayValue}
            </TippyToolTip>
          )}
        </Data>
      );
    });
  }

  // If the values is null, display N/A
  if (values === null) {
    return (
      <Data
        showColumn={showColumn}
        showRow={showRow}
        dataColor={dataColor || 'grey'}
      >
        N/A
      </Data>
    );
  }
};

const tooltipData = [
  {
    label: 'Skip rate',
    tooltipContent: `Skip rate includes tracks streamed between 30 and 60 seconds. So the
    "skips" are only skips reported after 30 seconds and before 1 minute.`,
  },
  {
    label: 'Views',
    tooltipContent: `The number of views of creations that use the song.`,
  },
  {
    label: 'Creations',
    tooltipContent: `The number of creations (videos) that use the song.`,
  },
];

interface IKeyValuePairProps extends IValuesProps {
  // Optional: When composing KeyValue Pair with react-emotion it needs to support a className prop. See https://github.com/emotion-js/emotion/blob/master/docs/styled.md#styling-any-component
  className?: string;
  // The label for the value.
  label: string;
  // The value to display. It is not required because it can, and may, be null.
  value?: any;
  // The item type option for number formatting
  type?: 'number' | 'percentage' | 'trend' | 'newTrend' | 'rawNumber';
  // data-pw value
  cy?: string;
  // row show data
  showRow?: boolean;
  // column show data
  showColumn?: boolean;
  // how to show data
  newDesign?: boolean;
  // show mobile view
  isMobileView?: boolean;
  breakdownAttributes?: any;
  withTooltip?: boolean;
  compareMode?: boolean;
  isKPIValue?: boolean;
  circleColor?: string;
  circleColorOpacity?: string;
  keyValueType?: 'rtd-access-statistics' | undefined;
  mobileRow?: boolean;
}

/**
 * Displays a key/value pair. The pair can have multiple values (an array of values) or have no values (null).
 */
const KeyValuePair: React.FC<IKeyValuePairProps> = (props) => {
  const {
    className,
    label,
    value,
    showRow,
    showColumn,
    isMobileView,
    type,
    newDesign,
    withTooltip = false,
    keyValueType,
    compareMode,
    mobileRow,
    ...breakdownAttributes
  } = props;
  const isPositive = value && value[0]?.value > 0;
  const dataWithTooltip = tooltipData.find((data: any) => data.label === label);

  return (
    <Group
      className={className}
      data-pw="key-value-group"
      showRow={showRow}
      showColumn={showColumn}
      compareNumber={value?.length ? value.length : 1}
      isMobileView={isMobileView && keyValueType !== 'rtd-access-statistics'}
      isHeightFixed={isMobileView && !!compareMode}
      mobileRow={mobileRow}
    >
      {isMobileView && keyValueType !== 'rtd-access-statistics' && (
        <StyledChangeArrow isMobileView={isMobileView} isPositive={isPositive}>
          <span aria-hidden>
            {isMobileView &&
              (type === 'newTrend' ||
                (value[0].type === 'trend' && newDesign)) &&
              !compareMode &&
              (isPositive ? '▲' : '▼')}
          </span>
        </StyledChangeArrow>
      )}
      {label && (
        <>
          {/* for premium/freemium breakdown mobile*/}
          {keyValueType === 'rtd-access-statistics' &&
            isMobileView &&
            breakdownAttributes.circleColor && (
              <Circle
                color={breakdownAttributes.circleColor}
                opacity={breakdownAttributes.circleColorOpacity}
                isMobileView={isMobileView}
              />
            )}
          <Label newDesign={newDesign} isMobileView={isMobileView}>
            {dataWithTooltip ? (
              <TippyToolTip
                tooltipContent={dataWithTooltip.tooltipContent}
                placement="bottom-start"
                maxWidth="445px"
                withDashedLine
                enabled
              >
                {label}
              </TippyToolTip>
            ) : (
              label
            )}
          </Label>
        </>
      )}
      <ValuesWrapper compareMode={compareMode} isMobileView={isMobileView}>
        <Values
          {...props}
          breakdownAttributes={breakdownAttributes}
          values={value}
          withTooltip={withTooltip}
        />
      </ValuesWrapper>
    </Group>
  );
};

export default KeyValuePair;
